import { Link, useTranslation } from "gatsby-plugin-react-i18next";
import * as React from "react";

import { navigate } from "gatsby";
import { useConfig } from "../../hooks/useConfig";
import { GtmEven, usePushGtmEvent } from "../../hooks/useGmtPushEvent";
import Button from "../form/Button";
import FileInput from "../form/FileInput";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  message: "",
  curriculum: null,
};

interface ApplyProps {
  jobPosition: object;
  classNames?: string;
}

const Apply: React.FunctionComponent<ApplyProps> = ({
  jobPosition,
  classNames = "",
}) => {
  const [formData, updateFormData] = React.useState(initialFormData);
  const [sending, setSending] = React.useState(false);
  const [showError, setShowError] = React.useState(false);

  const { jobApplication } = useConfig();
  const { t } = useTranslation();
  const pushGtmEvent = usePushGtmEvent();

  const onFormSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    setShowError(false);

    try {
      await fetch(jobApplication.endpoint, {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          curriculum: await fileToBase64(formData.curriculum),
          jobPosition,
        }),
      });
      navigate("/join/thanks");
      pushGtmEvent({ event: GtmEven.applyFormSubmit });
    } catch (e) {
      setShowError(true);
      setSending(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleFileUpload = async (file) =>
    updateFormData({
      ...formData,
      curriculum: file,
    });

  const handleFormChange = (event) =>
    updateFormData({
      ...formData,
      [event.target.name]: event.target.value.trim(),
    });

  return (
    <form onSubmit={onFormSubmit} className={classNames}>
      <div className="flex flex-wrap">
        <div className="pb-2 pr-2 w-1/2">
          <div className="relative">
            <label htmlFor="firstName" className="block text-sm text-gray-700">
              {t("join:joinForm.firstName")}
              <span className="ml-1 text-red-600">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              required
              onChange={handleFormChange}
              disabled={sending}
              className="w-full shadow-sm rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-gray-700 py-1 px-3 mt-1 leading-8 transition-colors duration-200 ease-in-out"
            />
          </div>
        </div>
        <div className="pb-2 pl-2 w-1/2">
          <div className="relative">
            <label htmlFor="lastName" className="block text-sm text-gray-700">
              {t("join:joinForm.lastName")}
              <span className="ml-1 text-red-600">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              required
              onChange={handleFormChange}
              disabled={sending}
              className="w-full shadow-sm rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-gray-700 py-1 px-3 mt-1 leading-8 transition-colors duration-200 ease-in-out"
            />
          </div>
        </div>
        <div className="py-2 w-full">
          <div className="relative">
            <label htmlFor="email" className="block text-sm text-gray-700">
              {t("join:joinForm.email")}
              <span className="ml-1 text-red-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              onChange={handleFormChange}
              disabled={sending}
              className="w-full shadow-sm rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-gray-700 py-1 px-3 mt-1 leading-8 transition-colors duration-200 ease-in-out"
            />
          </div>
        </div>
        <div className="py-2 w-full">
          <div className="relative">
            <label htmlFor="message" className="block text-sm text-gray-700">
              {t("join:joinForm.message")}
            </label>
            <textarea
              id="message"
              name="message"
              onChange={handleFormChange}
              disabled={sending}
              className="w-full shadow-sm rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 h-32 text-base outline-none text-gray-700 py-1 px-3 mt-1 resize-none leading-6 transition-colors duration-200 ease-in-out"
            ></textarea>
          </div>
        </div>
        <div className="py-2 w-full">
          <div className="relative">
            <label className="block text-sm text-gray-700">
              {t("join:joinForm.curriculum")}
              <span className="ml-1 text-red-600">*</span>
            </label>
            <FileInput
              placeholder={t("join:joinForm.curriculumPlaceholder")}
              onChange={handleFileUpload}
              disabled={sending}
              accept="application/pdf"
            />
          </div>
        </div>
        <div className="py-2 w-full">
          <Link to="/privacy/policy-hr">
            <label className="block text-sm text-indigo-500 cursor-pointer underline">
              {t("join:joinForm.privacy.label")}
            </label>
          </Link>
          <div className="flex space-x-2  items-center mt-2">
            <input
              type="checkbox"
              name="privacy1"
              required
              className="cursor-pointer h-6 w-6 shadow-sm rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-indigo-500 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
            />
            <label className="text-xs text-gray-500">
              {t("join:joinForm.privacy.check1")}
            </label>
          </div>
          <div className="flex space-x-2 items-center mt-2">
            <input
              type="checkbox"
              name="privacy1"
              required
              className="cursor-pointer h-6 w-6 shadow-sm rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-indigo-500 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
            />
            <label className="text-xs text-gray-500">
              {t("join:joinForm.privacy.check2")}
            </label>
          </div>
        </div>
        <div className="pt-6 w-full">
          {showError && (
            <div className="w-full mb-4 py-3 px-5 bg-red-50 rounded-md text-red-700">
              <span className="font-bold">
                {t("join:joinForm.error.title")}
              </span>{" "}
              {t("join:joinForm.error.message")}
            </div>
          )}
          <Button
            type="submit"
            loading={sending}
            disabled={sending}
            text={t("join:joinForm.send")}
            loadingText={t("join:joinForm.sending")}
          />
        </div>
      </div>
    </form>
  );
};

export default Apply;
